import React, { useState, useCallback } from 'react';
import type { UploadProps } from '../types';

import { useDropzone } from 'react-dropzone';
import { mergeClasses } from 'minimal-shared/utils';

import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import { Iconify } from 'src/components/iconify';

import { uploadClasses } from '../classes';
import { RejectedFiles } from '../components/rejected-files';
import { getFileMeta, useFilePreview } from '../../file-thumbnail';
import {
  UploadArea,
  PreviewImage,
  UploadContent,
  UploadWrapper,
  PlaceholderContainer,
} from './styles';
import { ImageCropperDialog } from './crop-dialog';

// ----------------------------------------------------------------------

export function UploadAvatar({
  sx,
  error,
  value,
  disabled,
  className,
  slotProps,
  helperText,
  loading = false,
  hideFilesRejected = false,
  ...dropzoneOptions
}: UploadProps) {
  const { onDrop, ...otherDropzoneOptions } = dropzoneOptions;

  const [selectedFileForCrop, setSelectedFileForCrop] = useState<File | null>(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file) {
        setSelectedFileForCrop(file);
        setCropDialogOpen(true);
      }
    },
    []
  );

  const handleCropComplete = useCallback(
    (croppedFile: File) => {
      if (onDrop) {
        onDrop([croppedFile], [], {} as any);
      }
    },
    [onDrop]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    disabled,
    multiple: false,
    accept: { 'image/*': [] },
    onDrop: handleDrop,
    ...otherDropzoneOptions,
  });

  const file = !Array.isArray(value) ? value : null;
  const hasSelectedFile = !!file;
  const hasError = isDragReject || !!error;
  const showFilesRejected = !hideFilesRejected && fileRejections.length > 0;

  const fileMeta = getFileMeta(file);
  const { previewUrl } = useFilePreview(file);

  const renderPlaceholder = () => (
    <PlaceholderContainer className={uploadClasses.placeholder.root}>
      <Iconify icon="solar:camera-add-bold" width={24} className={uploadClasses.placeholder.icon} />
      <Typography
        variant="caption"
        className={uploadClasses.placeholder.title}
        sx={{
          fontSize: '10px',
          textAlign: 'center',
          px: 0.5,
          lineHeight: 1.2,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          fontWeight: 'medium',
        }}
      >
        {hasSelectedFile ? 'Uppdatera foto' : 'Ladda upp foto'}
      </Typography>
    </PlaceholderContainer>
  );

  const renderLoading = () =>
    loading && (
      <CircularProgress
        thickness={1}
        size="100%"
        sx={{ zIndex: 9, top: 0, left: 0, position: 'absolute' }}
      />
    );

  const renderPreview = () =>
    hasSelectedFile && previewUrl && <PreviewImage alt={fileMeta.name} src={previewUrl} />;

  return (
    <>
      <UploadWrapper {...slotProps?.wrapper} className={uploadClasses.wrapper}>
        <UploadArea
          {...getRootProps()}
          className={mergeClasses([uploadClasses.avatar, className], {
            [uploadClasses.state.dragActive]: isDragActive,
            [uploadClasses.state.disabled]: disabled,
            [uploadClasses.state.error]: hasError,
            [uploadClasses.state.hasFile]: hasSelectedFile,
          })}
          sx={sx}
        >
          <input {...getInputProps()} />
          <UploadContent>
            {renderPreview()}
            {renderPlaceholder()}
          </UploadContent>
          {renderLoading()}
        </UploadArea>

        {helperText && helperText}
        {showFilesRejected && <RejectedFiles files={fileRejections} {...slotProps?.rejectedFiles} />}
      </UploadWrapper>

      <ImageCropperDialog
        open={cropDialogOpen}
        imageFile={selectedFileForCrop}
        onClose={() => setCropDialogOpen(false)}
        onCropComplete={handleCropComplete}
      />
    </>
  );
}

